import React, { useCallback, useEffect } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import { useParams } from "react-router-dom";
import { usePayoutStore } from "../../Store/Payout";
import Table, { Column } from "../../Components/Atoms/Table";
import { PayoutTransferView } from "../../Contracts/Transaction";
import { toLocalDateString } from "../../Utils/DateUtils";
import { useTeamStore } from "../../Store/Team";
import LoadingPage from "../LoadingPage";
import { ReactComponent as ExternalLinkIcon } from "../../Assets/ActionIcons/ExternalLinkIcon.svg";

const PayoutDetailPage: React.FC = () => {
  const { payout_id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading, getPayoutDetails, payoutDetails } = usePayoutStore();
  const { formatCurrency } = useTeamStore();
  const refreshThings = useCallback(async () => {
    payout_id && (await getPayoutDetails(getAccessTokenSilently, payout_id));
  }, [getAccessTokenSilently, getPayoutDetails, payout_id]);

  const columns: Column<PayoutTransferView>[] = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: d => <div className="capitalize">{d.type}</div>
    },
    {
      title: "Invoice/Quote",
      dataIndex: "invoice_quote",
      key: "invoice_quote",
      render: d =>
        d.data.transactionable_url ? (
          <a
            href={d.data.transactionable_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600"
          >
            {d.invoice_quote}
          </a>
        ) : (
          <div>{d.invoice_quote}</div>
        )
    },
    {
      title: "Transaction Reference",
      dataIndex: "transaction_reference",
      key: "transaction_reference"
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date"
    },
    {
      title: "Original Amount",
      dataIndex: "original_amount",
      key: "original_amount"
    },
    {
      title: "Surcharge",
      dataIndex: "surcharge",
      key: "surcharge"
    },
    {
      title: "Fee",
      dataIndex: "fee",
      key: "fee"
    },
    {
      title: "Net",
      dataIndex: "net",
      key: "net"
    }
  ];

  const dataSource =
    payoutDetails?.transfers.map(transfer => {
      return {
        key: transfer.transaction_reference,
        type: transfer.type,
        invoice_quote: transfer.transactionable,
        transaction_reference: transfer.transaction_reference,
        date: toLocalDateString(transfer.date),
        original_amount: formatCurrency(transfer.original_amount),
        surcharge: formatCurrency(transfer.surcharge),
        fee: formatCurrency(transfer.fee),
        net: formatCurrency(transfer.net),
        data: transfer
      };
    }) || [];

  useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  return (
    <PaymentsAppLayout documentTitle="Payouts">
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        <div className="overflow-y-auto flex-grow">
          {isLoading && payoutDetails?.transfers.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
        </div>
        <div className="flex justify-end border-t pt-4">
          <div className="flex flex-col">
            <div className="flex space-x-2 self-end font-semibold">
              <div>Total:</div>
              {payoutDetails?.total && <div>{formatCurrency(payoutDetails.total)}</div>}
            </div>
            {payoutDetails?.deposit_url && (
              <div className="self-end flex items-center text-blue-600 space-x-1 cursor-pointer">
                <a href={payoutDetails.deposit_url} target="_blank" rel="noreferrer">
                  {payoutDetails?.deposit_link_text || "View Transfer Details"}
                </a>
                <ExternalLinkIcon className="w-3 h-3" />
              </div>
            )}
          </div>
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(PayoutDetailPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
